import React, { useEffect, useState, useCallback } from "react";
import {
  Row,
  Col,
  Pagination,
  Collapse,
  Button,
  InputNumber,
  Space,
  Switch,
  Table,
  Tooltip,
  Typography,
  Form,
  Input,
  message,
  Select,
  Upload,
  Modal,
} from "antd";
import axios from "axios";
import PageHeader from "../Layout/PageHeader";
import {
  CaretRightOutlined,
  PlusOutlined,
  SaveOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { previewBase64Image } from "../../helpers/image";
import { formatDateTime } from "../../helpers/date";
import BannerFilters from "./BannerFilters";
import { PERMISSIONS } from "../../constants/roles";
import {
  BANNER_PLACEMENT,
  BANNER_PLACEMENT_LABEL,
} from "../../constants/bannerPlacement";
import { usePermissions } from "../App/PermissionContext";

// The upload lists are form values, so resetFields() clears them along with the rest of the form
const normFile = (e) => (Array.isArray(e) ? e : e?.fileList);

const uploadedFile = (fileList) => fileList?.[0]?.originFileObj;

const imageUploadProps = {
  beforeUpload: (file) => {
    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      message.error("File must be smaller than 5MB!");
      return Upload.LIST_IGNORE;
    }
    return false;
  },
  accept: "image/png,image/jpeg",
  listType: "picture-card",
  onPreview: (file) => previewBase64Image(file),
  maxCount: 1,
};

const Banner = () => {
  const { permissions } = usePermissions();

  const [pagination, setPagination] = useState({ currentPage: 1, limit: 10 });
  const [banners, setBanners] = useState([]);
  const [isActive, setIsActive] = useState(false);
  const [placement, setPlacement] = useState(
    BANNER_PLACEMENT.DASHBOARD_CAROUSEL
  );
  const [loading, setLoading] = useState({
    index: false,
    create: false,
    update: false,
  });
  const [filters, setFilters] = useState({
    isActive: null,
    placement: null,
  });
  const [form] = Form.useForm();

  const isTrackParcel = placement === BANNER_PLACEMENT.TRACK_PARCEL;

  const fetchBanner = useCallback(async () => {
    setLoading((prevLoading) => ({ ...prevLoading, index: true }));

    const params = {
      ...(filters.isActive !== null && { isActive: filters.isActive }),
      ...(filters.placement && { placement: filters.placement }),
      limit: pagination.limit,
      page: pagination.currentPage,
    };

    try {
      const res = await axios.get("/api/banners", { params });

      setBanners(res.data.data);
      setPagination(res.data.pagination);
    } catch (error) {
      message.error(error.response?.data?.message || error.message);
    }

    setLoading((prevLoading) => ({ ...prevLoading, index: false }));
  }, [pagination.currentPage, pagination.limit, filters]);

  const createHandler = async ({ image, mobileImage, ...newBanner }) => {
    setLoading((prevLoading) => ({ ...prevLoading, create: true }));

    try {
      const formData = new FormData();

      Object.entries(newBanner).forEach(([key, value]) => {
        formData.append(key, value);
      });

      formData.append("image", uploadedFile(image));

      if (isTrackParcel) {
        formData.append("mobileImage", uploadedFile(mobileImage));
      }

      await axios.post("/api/banners", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      form.resetFields();
      setPlacement(BANNER_PLACEMENT.DASHBOARD_CAROUSEL);
      message.success("Banner created");
      fetchBanner();
    } catch (error) {
      message.error(error.response?.data.message || error.message);
    }

    setLoading((prevLoading) => ({ ...prevLoading, create: false }));
  };

  const updateIsActiveStatus = async (row, value) => {
    setLoading((prevLoading) => ({ ...prevLoading, update: true }));

    await axios
      .patch(`/api/banner/${row.objectId}`, {
        isActive: value,
      })
      .then((res) => {
        message.info(`Banner ${value ? "Enabled" : "Disabled"}`);
        fetchBanner();
      })
      .catch((error) => {
        message.error(error.message);
      });

    setLoading((prevLoading) => ({ ...prevLoading, update: false }));
  };

  const updateBanner = async (row, params) => {
    setLoading((prevLoading) => ({ ...prevLoading, update: true }));

    await axios
      .patch(`/api/banner/${row.objectId}`, params)
      .then((res) => {
        message.info(`Banner sequence updated`);
        fetchBanner();
      })
      .catch((error) => {
        message.error(error.message);
      });

    setLoading((prevLoading) => ({ ...prevLoading, update: false }));
  };

  const archiveHandler = async (bannerId) => {
    try {
      const messageContent = (
        <p>
          Are you sure you want to delete this banner?
          <br />
        </p>
      );

      Modal.confirm({
        content: messageContent,
        title: `Delete Banner? `,
        async onOk() {
          await axios
            .delete(`/api/banner/${bannerId}`)
            .then((res) => {
              message.success(`Banner deleted.`);
              fetchBanner();
            })
            .catch((error) => {
              message.error(error.message);
            });
        },
      });
    } catch (error) {
      message.error(error.response.data.message || error.message);
    }
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      width: 100,
    },
    {
      title: "Placement",
      dataIndex: "placement",
      width: 150,
      // Banners created before placements existed have none and belong to the carousel
      render: (placement) =>
        BANNER_PLACEMENT_LABEL[placement || BANNER_PLACEMENT.DASHBOARD_CAROUSEL],
    },
    {
      title: "link",
      dataIndex: "link",
      width: 100,
      render: (text, row) => {
        return (
          <a href={row.link} target="_blank">
            <Typography.Link>{row.link}</Typography.Link>
          </a>
        );
      },
    },
    {
      title: "CTA (English)",
      dataIndex: "cta_en",
      width: 166,
      render: (text, row) => {
        return permissions.includes(PERMISSIONS.UPDATE_BANNER) ? (
          <Input
            value={text}
            onChange={(e) => {
              setBanners(
                banners.map((banner) =>
                  banner.objectId === row.objectId
                    ? { ...banner, cta_en: e.target.value }
                    : banner
                )
              );
            }}
          />
        ) : text
      }
    },
    {
      title: "CTA (Malay)",
      dataIndex: "cta_ms",
      width: 166,
      render: (text, row) => {
        return permissions.includes(PERMISSIONS.UPDATE_BANNER) ? (
          <Input
            value={text}
            onChange={(e) => {
              setBanners(
                banners.map((banner) =>
                  banner.objectId === row.objectId
                    ? { ...banner, cta_ms: e.target.value }
                    : banner
                )
              );
            }}
          />
        ) : text
      },
    },
    {
      title: "CTA (Chinese)",
      dataIndex: "cta_zh",
      width: 166,
      render: (text, row) => {
        return permissions.includes(PERMISSIONS.UPDATE_BANNER) ? (
          <Input
            value={text}
            onChange={(e) => {
              setBanners(
                banners.map((banner) =>
                  banner.objectId === row.objectId
                    ? { ...banner, cta_zh: e.target.value }
                    : banner
                )
              );
            }}
          />
        ) : text
      },
    },
    {
      title: "Sequence",
      dataIndex: "sequence",
      width: 100,
      render: (text, row) => {
        return permissions.includes(PERMISSIONS.UPDATE_BANNER) ? (
          <InputNumber
            value={text}
            precision={0}
            onChange={(value) => {
              setBanners(
                banners.map((banner) =>
                  banner.objectId === row.objectId
                    ? { ...banner, sequence: value }
                    : banner
                )
              );
            }}
          />
        ) : text
      }
    },
    {
      title: "Is Active",
      dataIndex: "isActive",
      width: 130,
      render: (text, row) => {
        return permissions.includes(PERMISSIONS.UPDATE_BANNER) ? (
          <Space>
            <Switch
              checked={text}
              onChange={(checked) => {
                updateIsActiveStatus(row, checked);
              }}
            />
          </Space>
        ) : text ? text : "-";
      }
    },
    {
      title: "Image",
      dataIndex: "Image",
      width: 100,
      render: (text, row) => {
        if (row.placement === BANNER_PLACEMENT.TRACK_PARCEL) {
          return (
            <Space direction="vertical" size={0}>
              <Typography.Link
                href={row.image}
                target="_blank"
                rel="noopener noreferrer"
              >
                Desktop
              </Typography.Link>
              {row.mobileImage && (
                <Typography.Link
                  href={row.mobileImage}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Mobile
                </Typography.Link>
              )}
            </Space>
          );
        }

        return (
          <a href={row.image} target="_blank">
            <Typography.Link>View</Typography.Link>
          </a>
        );
      },
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      width: 160,
      render: (createdAt) => formatDateTime(createdAt),
    },
    ...(permissions.includes(PERMISSIONS.UPDATE_BANNER) ? [{
      title: "",
      dataIndex: "action",
      fixed: "right",
      width: 100,
      render: (_, row) => (
        <Space>
          <Tooltip title="Update">
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={() =>
                updateBanner(row, {
                  sequence: row.sequence,
                  cta_en: row.cta_en,
                  cta_ms: row.cta_ms,
                  cta_zh: row.cta_zh,
                })
              }
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button
              type="primary"
              danger
              icon={<DeleteOutlined />}
              onClick={() => archiveHandler(row.objectId)}
            />
          </Tooltip>
        </Space>
      ),
    }] : [])

  ];

  useEffect(() => {
    fetchBanner();
  }, [pagination.currentPage, pagination.limit, fetchBanner]);

  return (
    <div>
      <PageHeader
        title="Banner"
        childRoutes={[{ path: "/banner", breadcrumbName: "Banner" }]}
      />

      {permissions.includes(PERMISSIONS.CREATE_BANNER) && (<Collapse
        expandIcon={({ isActive }) => (
          <CaretRightOutlined rotate={isActive ? 90 : 0} />
        )}
      >
        <Collapse.Panel header="Create New Banner" key="create-banner-section">
          <Form
            form={form}
            name="new-banner-form"
            labelCol={{ span: 8 }}
            wrapperCol={{ span: 16 }}
            autoComplete="off"
            onFinish={createHandler}
          >
            <Form.Item
              label="Name/Label (Must be unique)"
              name="name"
              rules={[{ required: true }]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              label="Placement"
              name="placement"
              initialValue={BANNER_PLACEMENT.DASHBOARD_CAROUSEL}
              rules={[{ required: true }]}
            >
              <Select onChange={setPlacement}>
                {Object.values(BANNER_PLACEMENT).map((value) => (
                  <Select.Option key={value} value={value}>
                    {BANNER_PLACEMENT_LABEL[value]}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label={isTrackParcel ? "Desktop Image" : "Attachments"}
              name="image"
              valuePropName="fileList"
              getValueFromEvent={normFile}
              rules={[{ required: true, message: "Please upload an image." }]}
              extra={
                isTrackParcel &&
                "For best results: 1098 × 2048 px (portrait, 549 × 1024 at 2×), max 1200 px wide. PNG/JPG, max 5MB. Keep text and logos centred; edges may be cropped on some screens."
              }
            >
              <Upload {...imageUploadProps}>
                <div>
                  <PlusOutlined />
                  <div style={{ marginTop: 8 }}>Upload</div>
                </div>
              </Upload>
            </Form.Item>

            {isTrackParcel && (
              <Form.Item
                label="Mobile Image"
                name="mobileImage"
                valuePropName="fileList"
                getValueFromEvent={normFile}
                // Dropped from the form values when the field unmounts, so switching placement never sends a hidden file
                preserve={false}
                rules={[
                  { required: true, message: "Please upload the mobile image." },
                ]}
                extra="For best results: 786 × 712 px (393 × 356 at 2×), max 1200 px wide. PNG/JPG, max 5MB. Keep text and logos centred."
              >
                <Upload {...imageUploadProps}>
                  <div>
                    <PlusOutlined />
                    <div style={{ marginTop: 8 }}>Upload</div>
                  </div>
                </Upload>
              </Form.Item>
            )}

            <Form.Item
              label="Link"
              name="link"
              extra={
                isTrackParcel &&
                "Opens in a new tab when the banner is clicked. Must be a full https:// URL."
              }
              rules={[
                { required: true },
                ...(isTrackParcel
                  ? [
                      {
                        pattern: /^https:\/\/\S+\.\S+$/,
                        message: "Link must be a full https:// URL.",
                      },
                    ]
                  : []),
              ]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              label="Active"
              name="isActive"
              rules={[{ required: true }]}
            >
              <Select value={isActive} onChange={setIsActive}>
                <Select.Option value={true}>Yes</Select.Option>
                <Select.Option value={false}>No</Select.Option>
              </Select>
            </Form.Item>

            <Form.Item
              label="CTA (English)"
              name="cta_en"
              rules={[{ required: true }]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              label="CTA (Malay)"
              name="cta_ms"
              rules={[{ required: true }]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              label="CTA (Chinese)"
              name="cta_zh"
              rules={[{ required: true }]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              label="Sequence"
              name="sequence"
              rules={[{ required: true }]}
            >
              <InputNumber step={1} />
            </Form.Item>

            <Form.Item wrapperCol={{ offset: 8, span: 16 }}>
              <Button loading={loading.create} type="primary" htmlType="submit">
                Create
              </Button>
            </Form.Item>
          </Form>
        </Collapse.Panel>
      </Collapse>)}

      <Collapse
        expandIcon={({ isActive }) => (
          <CaretRightOutlined rotate={isActive ? 90 : 0} />
        )}
      >
        <Collapse.Panel header="Filters" key="filters">
          <BannerFilters
            filters={filters}
            onFiltersChange={(filters) => {
              setFilters(filters);
              // A narrower filter may have fewer pages than the one being viewed
              setPagination((prev) => ({ ...prev, currentPage: 1 }));
            }}
          />
        </Collapse.Panel>
      </Collapse>



      <Row justify="end">
        <Col>
          <Pagination
            total={pagination?.totalSize}
            pageSize={pagination?.limit}
            onChange={(currentPage, limit) => {
              setPagination({ ...pagination, limit, currentPage });
            }}
            current={pagination.currentPage}
          />
        </Col>
      </Row>

      <Table
        rowKey="bannerId"
        pagination={false}
        columns={columns}
        dataSource={banners}
        loading={loading.index || loading.update || loading.create}
        scroll={{ x: 1000, y: 500 }}
      />

      <Row justify="end">
        <Col>
          <Pagination
            total={pagination?.totalSize}
            pageSize={pagination?.limit}
            onChange={(currentPage, limit) => {
              setPagination({ ...pagination, limit, currentPage });
            }}
            current={pagination.currentPage}
          />
        </Col>
      </Row>
    </div>
  );
};

export default Banner;
